import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faClockRotateLeft, 
    faExclamationTriangle, 
    faCircleDot, 
    faCheckCircle, 
    faLocationDot, 
    faMessageLines, 
    faShip,
    faTruck
} from '@fortawesome/pro-light-svg-icons';

export interface ITrackingNoteProgressItem {
    id: string;
    rawText: string;
    note: string;
    date?: string;
    statusLabel?: string;
    isReschedule?: boolean;
    reason?: string;
}

/**
 * Helper untuk mem-parse string notes tracking menjadi list entri timeline berurutan.
 * Mendukung format:
 * - Entri tunggal teks biasa (misal: "kapal berlayar")
 * - Entri reschedule lama (misal: "[Reschedule: Bad Weather] Gempa cuyyy")
 * - Entri beruntun dipisahkan oleh "\n---\n" atau separator baris baru
 * - Entri ber-timestamp: "[DD/MM/YYYY HH:mm | In Progress] kapal bersandar"
 */
export const parseTrackingNotes = (rawNotes?: string): ITrackingNoteProgressItem[] => {
    if (!rawNotes || typeof rawNotes !== 'string' || rawNotes.trim() === '') {
        return [];
    }

    // Split berdasarkan separator "---" yang lazim
    let rawEntries = rawNotes
        .split(/\r?\n\s*---\s*\r?\n|\r?\n\s*---\s*$|^\s*---\s*\r?\n/)
        .map(e => e.trim())
        .filter(e => e.length > 0);

    // Fallback: Jika tidak ada separator "---" tetapi ada beberapa tag kurung siku berulang di baris baru
    if (rawEntries.length === 1 && (rawEntries[0].match(/\[(?:Reschedule|\d{2}\/\d{2}\/\d{4})/g) || []).length > 1) {
        rawEntries = rawEntries[0].split(/(?=\[(?:Reschedule|\d{2}\/\d{2}\/\d{4}))/g).map(e => e.trim()).filter(Boolean);
    }

    return rawEntries.map((entry, idx) => {
        // 1. Format: [DD/MM/YYYY HH:mm | Rescheduled: Reason] Note text
        const matchReschedWithDate = entry.match(/^\[([^\|\]]+)\s*\|\s*Reschedule(?::\s*([^\]]+))?\]\s*([\s\S]*)$/i);
        if (matchReschedWithDate) {
            return {
                id: `note_step_${idx}`,
                rawText: entry,
                date: matchReschedWithDate[1].trim(),
                isReschedule: true,
                reason: matchReschedWithDate[2]?.trim() || 'Rescheduled',
                statusLabel: 'Rescheduled',
                note: matchReschedWithDate[3]?.trim() || '',
            };
        }

        // 2. Format: [Reschedule: Reason] Note text
        const matchReschedSimple = entry.match(/^\[Reschedule(?::\s*([^\]]+))?\]\s*([\s\S]*)$/i);
        if (matchReschedSimple) {
            return {
                id: `note_step_${idx}`,
                rawText: entry,
                isReschedule: true,
                reason: matchReschedSimple[1]?.trim() || 'Rescheduled',
                statusLabel: 'Rescheduled',
                note: matchReschedSimple[2]?.trim() || '',
            };
        }

        // 3. Format: [DD/MM/YYYY HH:mm | Status] Note text
        const matchDateAndStatus = entry.match(/^\[([^\|\]]+)\s*\|\s*([^\]]+)\]\s*([\s\S]*)$/);
        if (matchDateAndStatus) {
            const statusStr = matchDateAndStatus[2].trim();
            const isResched = statusStr.toLowerCase().includes('reschedule');
            return {
                id: `note_step_${idx}`,
                rawText: entry,
                date: matchDateAndStatus[1].trim(),
                statusLabel: statusStr,
                isReschedule: isResched,
                note: matchDateAndStatus[3]?.trim() || '',
            };
        }

        // 4. Format: [DD/MM/YYYY HH:mm] Note text
        const matchDateOnly = entry.match(/^\[(\d{2}\/\d{2}\/\d{4}(?:\s+\d{2}:\d{2})?)\]\s*([\s\S]*)$/);
        if (matchDateOnly) {
            return {
                id: `note_step_${idx}`,
                rawText: entry,
                date: matchDateOnly[1].trim(),
                note: matchDateOnly[2]?.trim() || '',
            };
        }

        // 5. Plain note text
        return {
            id: `note_step_${idx}`,
            rawText: entry,
            note: entry.trim(),
        };
    });
};

interface ITrackingNotesTimelineProps {
    notes?: string;
    rescheduleInfo?: {
        isRescheduled: boolean;
        reason?: string;
        notes?: string;
        fullNote?: string;
    };
}

export const TrackingNotesTimeline: React.FC<ITrackingNotesTimelineProps> = ({ notes, rescheduleInfo }) => {
    const items = parseTrackingNotes(notes);

    // Jika tidak ada parsed items dari string notes, tetapi ada rescheduleInfo objek terpisah
    if (items.length === 0 && rescheduleInfo && rescheduleInfo.isRescheduled) {
        items.push({
            id: 'resched_prop_0',
            rawText: rescheduleInfo.fullNote || '',
            isReschedule: true,
            reason: rescheduleInfo.reason || 'Rescheduled',
            statusLabel: 'Rescheduled',
            note: rescheduleInfo.notes || '',
        });
    }

    if (items.length === 0) {
        return null;
    }

    // Jika HANYA ADA 1 item dan BUKAN reschedule (misal teks catatan sederhana 1 baris)
    if (items.length === 1 && !items[0].isReschedule && !items[0].date && !items[0].statusLabel) {
        return (
            <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <FontAwesomeIcon icon={faMessageLines} style={{ color: '#94a3b8', fontSize: 12, marginTop: 3 }} />
                    <span style={{ fontSize: '12px', fontStyle: 'italic', color: '#475569', lineHeight: 1.4 }}>
                        {items[0].note}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            marginTop: 8,
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '12px 14px',
        }}>
            {/* Header Timeline */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FontAwesomeIcon icon={faClockRotateLeft} style={{ color: '#475569', fontSize: 12 }} />
                    <span style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#334155',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px'
                    }}>
                        Catatan & Progres Status
                    </span>
                </div>
                <span style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    color: '#64748b',
                    backgroundColor: '#e2e8f0',
                    padding: '1px 6px',
                    borderRadius: '10px'
                }}>
                    {items.length} Update
                </span>
            </div>

            {/* Timeline Process List */}
            <div style={{ position: 'relative', paddingLeft: 4 }}>
                {items.map((item, idx) => {
                    const isLast = idx === items.length - 1;
                    const isResched = item.isReschedule;

                    // Dot & Badge styling
                    const dotBg = isResched ? '#f59e0b' : (isLast ? '#10b981' : '#3b82f6');
                    const badgeBg = isResched ? '#fef3c7' : '#e0f2fe';
                    const badgeColor = isResched ? '#92400e' : '#0369a1';
                    const badgeBorder = isResched ? '#fde68a' : '#bae6fd';

                    return (
                        <div 
                            key={item.id}
                            style={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'flex-start',
                                paddingBottom: isLast ? 0 : 14,
                            }}
                        >
                            {/* Vertical Connector Line */}
                            {!isLast && (
                                <div 
                                    style={{
                                        position: 'absolute',
                                        left: 7,
                                        top: 14,
                                        bottom: 0,
                                        width: 2,
                                        backgroundColor: '#cbd5e1',
                                    }} 
                                />
                            )}

                            {/* Timeline Node Dot */}
                            <div 
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    border: `3px solid ${dotBg}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    marginTop: 2,
                                    zIndex: 2,
                                    boxShadow: '0 0 0 2px #fff',
                                }}
                            />

                            {/* Node Content */}
                            <div style={{ marginLeft: 10, flex: 1 }}>
                                {/* Header: Status / Tag & Date */}
                                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 2 }}>
                                    {isResched ? (
                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            backgroundColor: badgeBg,
                                            border: `1px solid ${badgeBorder}`,
                                            borderRadius: 4,
                                            padding: '1px 6px',
                                        }}>
                                            <span style={{ fontSize: 10 }}>⚠️</span>
                                            <span style={{
                                                fontSize: '10px',
                                                fontWeight: 700,
                                                color: badgeColor,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px'
                                            }}>
                                                Rescheduled
                                            </span>
                                            {item.reason && (
                                                <span style={{
                                                    fontSize: '10px',
                                                    fontWeight: 600,
                                                    color: '#b45309',
                                                }}>
                                                    • {item.reason}
                                                </span>
                                            )}
                                        </div>
                                    ) : item.statusLabel ? (
                                        <span style={{
                                            fontSize: '10px',
                                            fontWeight: 600,
                                            backgroundColor: badgeBg,
                                            color: badgeColor,
                                            border: `1px solid ${badgeBorder}`,
                                            borderRadius: 4,
                                            padding: '1px 6px',
                                        }}>
                                            {item.statusLabel}
                                        </span>
                                    ) : (
                                        <span style={{
                                            fontSize: '10px',
                                            fontWeight: 600,
                                            color: '#64748b'
                                        }}>
                                            Catatan #{idx + 1}
                                        </span>
                                    )}

                                    {item.date && (
                                        <span style={{
                                            fontSize: '10px',
                                            color: '#64748b',
                                            fontWeight: 500
                                        }}>
                                            ({item.date})
                                        </span>
                                    )}
                                </div>

                                {/* Note Body */}
                                {item.note && item.note.trim() !== '' && (
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#334155',
                                        lineHeight: 1.4,
                                        marginTop: 2,
                                        wordBreak: 'break-word'
                                    }}>
                                        {item.note}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default TrackingNotesTimeline;

